<template>
  <div v-if="loading" :class="['dpf-results__list', browseList ? 'dpf-results__list--browse' : null]">
    <div
      v-for="n in 5"
      :key="n"
      :class="['dpf-results__skeleton-row', browseList ? 'dpf-results__skeleton-row--browse' : null]"
    >
      <div class="dpf-results__skeleton-icon" />
      <div class="dpf-results__skeleton-text" :style="{ width: skeletonWidths[n - 1] + 'px' }" />
    </div>
  </div>

  <div
    v-else-if="departments.length"
    :class="['dpf-results__list', browseList ? 'dpf-results__list--browse' : 'dpf-results__list--scroll']"
  >
    <div
      v-for="dept in departments"
      :key="dept.id"
      :class="['dpf-results__row', browseList ? 'dpf-results__row--browse' : null]"
      :style="browseList ? browseRowStyle(dept) : undefined"
      role="option"
      @click.stop="$emit('select', dept)"
    >
      <Icon name="department_picker_icon" class="dpf-results__row-icon" />
      <div class="dpf-results__row-path">
        <span
          v-for="(seg, i) in getPathSegments(dept)"
          :key="i"
          class="dpf-results__row-path-item"
        >
          <span :class="pathSegmentClass(dept, i)">{{ seg }}</span>
          <span v-if="i < getPathSegments(dept).length - 1" class="dpf-results__row-path-sep">›</span>
        </span>
      </div>
      <span class="dpf-results__row-code">#{{ dept.code }}</span>
    </div>
  </div>

  <div v-else class="dpf-results__list dpf-results__empty">
    <p class="dpf-results__empty-title">No matches</p>
    <p class="dpf-results__empty-sub">
      {{ hasQuery ? "Try a different name or code" : "No departments available" }}
    </p>
  </div>
</template>

<script setup>
import Icon from "@components/icons/Icon.vue";
import {
  getDepartmentDepth,
  getPathSegments,
} from "./departmentPickerFieldUtils.js";

defineProps({
  departments: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  browseList: { type: Boolean, default: false },
  hasQuery: { type: Boolean, default: false },
});

defineEmits(["select"]);

const skeletonWidths = [180, 150, 210, 170, 140];

function browseRowStyle(dept) {
  const indent = getDepartmentDepth(dept) * 24;
  return {
    marginLeft: `${indent}px`,
    width: indent ? `calc(100% - ${indent}px)` : "100%",
  };
}

function pathSegmentClass(dept, index) {
  const segments = getPathSegments(dept);
  return index === segments.length - 1
    ? "dpf-results__row-path-leaf"
    : "dpf-results__row-path-ancestor";
}
</script>

<style scoped>
.dpf-results__list {
  padding: 4px;
  box-sizing: border-box;
}

.dpf-results__list--scroll,
.dpf-results__list--browse {
  max-height: 252px;
  overflow-y: auto;
}

.dpf-results__list--browse {
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dpf-results__row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  background: #fff;
  border-radius: 10px;
  cursor: pointer;
  box-sizing: border-box;
  margin-bottom: 4px;
}

.dpf-results__row--browse {
  margin-bottom: 0;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
  margin-left: 0px !important;
  width: 100% !important;
}

.dpf-results__row:last-child {
  margin-bottom: 0;
}

.dpf-results__row:hover {
  background: #f9fafb;
}

.dpf-results__row-icon {
  flex-shrink: 0;
  opacity: 0.6;
}

.dpf-results__row-path {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
}

.dpf-results__row-path-item {
  display: inline-flex;
  align-items: center;
}

.dpf-results__row-path-ancestor {
  color: #667085;
  font-weight: 400;
}

.dpf-results__row-path-sep {
  color: #d0d5dd;
  font-weight: 400;
  margin: 0 6px;
}

.dpf-results__row-path-leaf {
  color: #101828;
  font-weight: 600;
}

.dpf-results__row-code {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 400;
  color: #98a2b3;
  line-height: 20px;
  white-space: nowrap;
}

.dpf-results__skeleton-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  height: 40px;
  box-sizing: border-box;
}

.dpf-results__skeleton-row--browse {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
}

.dpf-results__skeleton-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  background: #eaecf0;
}

.dpf-results__skeleton-text {
  height: 12px;
  border-radius: 999px;
  background: #eaecf0;
  flex-shrink: 0;
}

.dpf-results__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 252px;
  gap: 4px;
}

.dpf-results__empty-title {
  font-size: 14px;
  font-weight: 500;
  color: #475467;
  margin: 0;
}

.dpf-results__empty-sub {
  font-size: 13px;
  font-weight: 400;
  color: #98a2b3;
  margin: 0;
}
</style>
